import React, { useCallback, useEffect, useState } from "react";
import { useGame } from "@/contexts/GameContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Package, Recycle, Sparkles, Loader2, Gift, Lock, Clock, Coins, ArrowRight, Star } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const PACK_META = {
  silver:    { border: "#c0c0c0", glow: "rgba(192,192,192,0.4)", gradient: "from-slate-300/20 to-slate-500/10" },
  gold:      { border: "#cfb53b", glow: "rgba(207,181,59,0.5)",  gradient: "from-[#cfb53b]/25 to-[#cfb53b]/10" },
  prismatic: { border: "#a855f7", glow: "rgba(168,85,247,0.6)",  gradient: "from-fuchsia-500/20 via-purple-500/20 to-cyan-400/20" },
};

const DUST_PACK_META = {
  small:  { icon: Recycle, label_en: "Small Pouch", label_it: "Piccolo Sacco" },
  medium: { icon: Recycle, label_en: "Merchant Chest", label_it: "Scrigno da Mercante", bestValue: false, label_badge_it: "+20%", label_badge_en: "+20%" },
  large:  { icon: Recycle, label_en: "Mage's Hoard",   label_it: "Tesoro del Mago", bestValue: true, label_badge_it: "+43%", label_badge_en: "+43%" },
};

export default function Packs() {
  const { game, loadGame } = useGame();
  const { token, API } = useAuth();
  const [packs, setPacks] = useState([]);
  const [currentDust, setCurrentDust] = useState(0);
  const [dustPacks, setDustPacks] = useState([]);
  const [currentGold, setCurrentGold] = useState(0);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(null); // pack id being opened
  const [buyingDust, setBuyingDust] = useState(null);

  const lang = game?.language || "en";
  const isIt = lang.startsWith("it");

  // Iter 100 — live drop-rates so the shop reflects admin-managed odds.
  const [dropRates, setDropRates] = useState(null);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/config/drop-rates`).then((r) => {
      if (alive) setDropRates(r.data);
    }).catch(() => {});
    return () => { alive = false; };
  }, [API]);

  const fetchAll = useCallback(async () => {
    if (!game?.id) return;
    setLoading(true);
    try {
      const [packRes, dustRes] = await Promise.all([
        axios.get(`${API}/game/${game.id}/packs`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/game/${game.id}/dust-packs`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setPacks(packRes.data.packs || []);
      setCurrentDust(packRes.data.current_dust || 0);
      setDustPacks(dustRes.data.packs || []);
      setCurrentGold(dustRes.data.current_gold || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load shop");
    } finally {
      setLoading(false);
    }
  }, [game?.id, API, token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleOpen = async (packId) => {
    if (opening) return;
    setOpening(packId);
    try {
      const res = await axios.post(
        `${API}/game/${game.id}/packs/${packId}/open`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const cards = res.data.cards || [];
      toast.success(
        isIt
          ? `Pack aperto! ${cards.length} carte ottenute`
          : `Pack opened! ${cards.length} cards obtained`,
        { duration: 4000 }
      );
      // Refresh game state → CardRevealOverlay will auto-trigger for special cards
      await loadGame(game.id);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to open pack");
    } finally {
      setOpening(null);
    }
  };

  const handleBuyDust = async (packId) => {
    if (buyingDust) return;
    setBuyingDust(packId);
    try {
      const res = await axios.post(
        `${API}/game/${game.id}/buy-dust`,
        { pack: packId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        isIt
          ? `+${res.data.dust_gained} polvere ottenuta`
          : `+${res.data.dust_gained} dust obtained`
      );
      await loadGame(game.id);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purchase failed");
    } finally {
      setBuyingDust(null);
    }
  };

  if (!game) return null;

  return (
    <div className="mb-4 sm:mb-6 bg-gradient-to-br from-[#1a1a1d]/70 to-[#0a0a0c]/90 border border-[#cfb53b]/25 rounded-lg p-3 sm:p-4" data-testid="packs-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading text-sm sm:text-base text-[#cfb53b] flex items-center gap-2">
          <Gift size={16} /> {isIt ? "Bustine di Carte" : "Card Packs"}
        </h3>
        <div className="flex items-center gap-1.5 bg-purple-500/10 border border-purple-500/30 rounded-full px-2.5 py-1">
          <Recycle size={12} className="text-purple-400" />
          <span className="font-heading text-xs text-purple-300">{currentDust}</span>
          <span className="text-[10px] text-[#94a3b8]">{isIt ? "polvere" : "dust"}</span>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-6"><Loader2 size={20} className="animate-spin text-[#cfb53b]" /></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {packs.map(pack => {
            const meta = PACK_META[pack.id] || PACK_META.silver;
            const unavailable = !pack.can_open_today;
            const unaffordable = !pack.affordable;
            const disabled = unavailable || unaffordable || opening !== null;
            const label = isIt ? pack.name_it : pack.name_en;
            return (
              <div
                key={pack.id}
                data-testid={`pack-${pack.id}`}
                className={`relative bg-gradient-to-br ${meta.gradient} rounded-lg p-3 border-2 transition-all ${
                  disabled ? "opacity-70" : "hover:scale-[1.02]"
                }`}
                style={{
                  borderColor: meta.border,
                  boxShadow: disabled ? "none" : `0 0 24px ${meta.glow}`,
                }}
              >
                {pack.id === "prismatic" && (
                  <div className="absolute inset-0 rounded-lg card-foil pointer-events-none opacity-40" />
                )}
                <div className="relative z-10">
                  <div className="flex items-center justify-center mb-2">
                    <Package size={36} style={{ color: meta.border }} />
                  </div>
                  <h4 className="font-heading text-sm text-center mb-1" style={{ color: meta.border }}>
                    {label}
                  </h4>
                  <div className="text-center text-[10px] text-[#94a3b8] space-y-0.5 mb-3">
                    <p>
                      {pack.cards} {isIt ? "carte" : "cards"} · {isIt ? "min" : "min"}{" "}
                      <span style={{ color: meta.border }} className="font-heading uppercase">
                        {pack.guaranteed_min_rarity}
                      </span>
                    </p>
                    {(() => {
                      // Iter 100 — display the LIVE foil chance from admin
                      // config. Fall back to the pack's hardcoded value.
                      const liveFoilChance = dropRates?.packs?.[pack.id]?.foil_chance ?? pack.foil_chance;
                      return (
                        <p>
                          {pack.guaranteed_foils > 0
                            ? <>✨ {pack.guaranteed_foils} foil {isIt ? "garantit" : "guaranteed"}{pack.guaranteed_foils > 1 ? (isIt?"i":"s"):(isIt?"a":"")}</>
                            : <>{Math.round(liveFoilChance * 100)}% foil/{isIt ? "carta" : "card"}</>
                          }
                        </p>
                      );
                    })()}
                    {/* Live rarity odds breakdown */}
                    {dropRates?.packs?.[pack.id]?.rarity_weights && (
                      <div className="flex flex-wrap items-center justify-center gap-1 pt-1">
                        {["common", "uncommon", "rare", "epic", "legendary"].map((r) => {
                          const w = dropRates.packs[pack.id].rarity_weights[r] || 0;
                          if (w < 0.005) return null;
                          const color = {
                            common: "#94a3b8",
                            uncommon: "#34d399",
                            rare: "#60a5fa",
                            epic: "#c084fc",
                            legendary: "#fbbf24",
                          }[r];
                          return (
                            <span
                              key={r}
                              className="px-1 py-0.5 rounded text-[9px] font-heading"
                              style={{ color, background: `${color}22`, border: `1px solid ${color}44` }}
                              title={isIt
                                ? `${r}: ${(w*100).toFixed(1)}% (esclude la carta garantita)`
                                : `${r}: ${(w*100).toFixed(1)}% (excludes the guaranteed card)`}
                            >
                              {(w * 100).toFixed(0)}%
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <Button
                    data-testid={`open-pack-${pack.id}`}
                    onClick={() => handleOpen(pack.id)}
                    disabled={disabled}
                    className="w-full font-heading text-xs h-8 transition-all"
                    style={{
                      background: disabled ? "rgba(255,255,255,0.05)" : `${meta.border}22`,
                      color: disabled ? "#64748b" : meta.border,
                      border: `1px solid ${disabled ? "#2d3748" : meta.border + "66"}`,
                    }}
                  >
                    {opening === pack.id ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : unavailable ? (
                      <><Clock size={12} className="mr-1" />{isIt ? "Domani" : "Tomorrow"}</>
                    ) : unaffordable ? (
                      <><Lock size={12} className="mr-1" />{pack.cost_dust}<Recycle size={11} className="ml-1" /></>
                    ) : (
                      <><Sparkles size={12} className="mr-1" />{pack.cost_dust}<Recycle size={11} className="ml-1" /></>
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="text-[10px] text-[#64748b] italic text-center mt-2">
        {isIt
          ? "1 acquisto per tipo al giorno · reset a mezzanotte UTC"
          : "1 purchase per type per day · resets at UTC midnight"}
      </p>

      {/* ============ DUST EXCHANGE ============ */}
      <div className="mt-5 pt-5 border-t border-[#cfb53b]/15">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-sm sm:text-base text-purple-300 flex items-center gap-2">
            <Recycle size={16} /> {isIt ? "Scambia Oro per Polvere" : "Gold → Dust Exchange"}
          </h3>
          <div className="flex items-center gap-1.5 bg-[#cfb53b]/10 border border-[#cfb53b]/30 rounded-full px-2.5 py-1">
            <Coins size={12} className="text-[#cfb53b]" />
            <span className="font-heading text-xs text-[#cfb53b]">{currentGold}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {dustPacks.map(dp => {
            const meta = DUST_PACK_META[dp.id] || DUST_PACK_META.small;
            const Icon = meta.icon;
            const disabled = !dp.affordable || buyingDust !== null;
            const label = isIt ? meta.label_it : meta.label_en;
            const isBest = meta.bestValue;
            return (
              <div
                key={dp.id}
                data-testid={`dust-pack-${dp.id}`}
                className={`relative bg-[#0a0a0c]/70 border rounded-lg p-3 transition-all ${
                  isBest ? "border-purple-400/50" : "border-[#2d3748]"
                } ${disabled ? "opacity-70" : "hover:border-purple-400/70"}`}
              >
                {isBest && (
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white text-[9px] font-heading px-2 py-0.5 rounded-full flex items-center gap-1 shadow-lg">
                    <Star size={9} fill="currentColor" />
                    {isIt ? "MIGLIOR VALORE" : "BEST VALUE"}
                  </div>
                )}
                <div className="flex items-center justify-center mb-2 mt-1">
                  <div className="relative">
                    <Icon size={32} className="text-purple-400" />
                    {meta.label_badge_it && (
                      <span className="absolute -top-1 -right-4 bg-purple-500/20 text-purple-300 text-[9px] font-heading px-1 rounded">
                        {isIt ? meta.label_badge_it : meta.label_badge_en}
                      </span>
                    )}
                  </div>
                </div>
                <h4 className="font-heading text-xs sm:text-sm text-center text-[#e2e8f0] mb-1">{label}</h4>
                <div className="text-center mb-3 flex items-center justify-center gap-1.5 text-xs">
                  <span className="text-[#cfb53b] font-heading">
                    <Coins size={11} className="inline mr-0.5 mb-0.5" />{dp.gold_cost}
                  </span>
                  <ArrowRight size={11} className="text-[#64748b]" />
                  <span className="text-purple-300 font-heading">
                    <Recycle size={11} className="inline mr-0.5 mb-0.5" />{dp.dust_gained}
                  </span>
                </div>
                <Button
                  data-testid={`buy-dust-${dp.id}`}
                  onClick={() => handleBuyDust(dp.id)}
                  disabled={disabled}
                  size="sm"
                  className="w-full bg-purple-500/10 text-purple-300 hover:bg-purple-500/20 border border-purple-500/30 h-8 text-xs font-heading"
                >
                  {buyingDust === dp.id ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : !dp.affordable ? (
                    <><Lock size={11} className="mr-1" />{isIt ? "Oro insufficiente" : "Not enough gold"}</>
                  ) : (
                    <><Sparkles size={11} className="mr-1" />{isIt ? "Compra" : "Buy"}</>
                  )}
                </Button>
              </div>
            );
          })}
        </div>
        <p className="text-[10px] text-[#64748b] italic text-center mt-2">
          {isIt
            ? "Più grande è lo scambio, migliore è il cambio (fino a +43% di polvere)"
            : "The larger the exchange, the better the rate (up to +43% dust)"}
        </p>
      </div>
    </div>
  );
}
